import { getLenis } from './lenis';

export class Sidebar {
    static scrollLockCount = 0;
    static pageScrollState = null;

    static lockPageScroll() {
        if (Sidebar.scrollLockCount === 0) {
            const scrollX = window.scrollX || window.pageXOffset || 0;
            const scrollY = window.scrollY || window.pageYOffset || 0;
            const scrollbarOffset =
                window.innerWidth - document.documentElement.clientWidth;
            const supportsStableScrollbarGutter = CSS.supports(
                'scrollbar-gutter: stable'
            );

            Sidebar.pageScrollState = {
                scrollX,
                scrollY,
                htmlOverflow: document.documentElement.style.overflow,
                htmlOverscrollBehavior:
                    document.documentElement.style.overscrollBehavior,
                htmlScrollBehavior:
                    document.documentElement.style.scrollBehavior,
                bodyOverflow: document.body.style.overflow,
                bodyPosition: document.body.style.position,
                bodyTop: document.body.style.top,
                bodyLeft: document.body.style.left,
                bodyRight: document.body.style.right,
                bodyWidth: document.body.style.width,
                bodyPaddingRight: document.body.style.paddingRight,
                bodyOverscrollBehavior: document.body.style.overscrollBehavior,
                bodyScrollBehavior: document.body.style.scrollBehavior,
            };

            document.documentElement.classList.add('overflow-hidden');
            document.body.classList.add('overflow-hidden');
            document.documentElement.style.overflow = 'hidden';
            document.documentElement.style.overscrollBehavior = 'none';
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollY}px`;
            document.body.style.left = '0';
            document.body.style.right = '0';
            document.body.style.width = '100%';
            document.body.style.overscrollBehavior = 'none';

            if (!supportsStableScrollbarGutter && scrollbarOffset > 0) {
                document.body.style.paddingRight = `${scrollbarOffset}px`;
            }

            getLenis()?.stop();
        }

        Sidebar.scrollLockCount += 1;
    }

    static unlockPageScroll() {
        Sidebar.scrollLockCount = Math.max(0, Sidebar.scrollLockCount - 1);

        if (Sidebar.scrollLockCount === 0) {
            const pageScrollState = Sidebar.pageScrollState;

            document.documentElement.classList.remove('overflow-hidden');
            document.body.classList.remove('overflow-hidden');
            document.documentElement.style.overflow =
                pageScrollState?.htmlOverflow || '';
            document.documentElement.style.overscrollBehavior =
                pageScrollState?.htmlOverscrollBehavior || '';
            document.documentElement.style.scrollBehavior = 'auto';
            document.body.style.overflow = pageScrollState?.bodyOverflow || '';
            document.body.style.position = pageScrollState?.bodyPosition || '';
            document.body.style.top = pageScrollState?.bodyTop || '';
            document.body.style.left = pageScrollState?.bodyLeft || '';
            document.body.style.right = pageScrollState?.bodyRight || '';
            document.body.style.width = pageScrollState?.bodyWidth || '';
            document.body.style.paddingRight =
                pageScrollState?.bodyPaddingRight || '';
            document.body.style.overscrollBehavior =
                pageScrollState?.bodyOverscrollBehavior || '';
            document.body.style.scrollBehavior = 'auto';
            getLenis()?.start();

            if (pageScrollState) {
                window.scrollTo(
                    pageScrollState.scrollX,
                    pageScrollState.scrollY
                );
            }

            requestAnimationFrame(() => {
                document.documentElement.style.scrollBehavior =
                    pageScrollState?.htmlScrollBehavior || '';
                document.body.style.scrollBehavior =
                    pageScrollState?.bodyScrollBehavior || '';
            });

            Sidebar.pageScrollState = null;
        }
    }

    constructor(options = {}) {
        this.options = {
            lockScroll: true,
            ...options,
        };
        this.init();
    }

    init() {
        this.root = null;
        this.content = null;
        this.overlay = null;
        this.isOpen = false;

        this.create();
        this.events();
    }

    create = () => {
        const rootClassNames = [
            'fixed',
            'top-0',
            'left-0',
            'w-full',
            'h-full',
            'z-200',
            'pointer-events-none',
            'group',
            '[&.-active]:pointer-events-auto',
            ...(this.options.rootClassNames || []),
        ];

        this.root = document.createElement('div');
        this.root.classList.add(...rootClassNames);

        const overlayClassNames = [
            'absolute',
            'top-0',
            'left-0',
            'w-full',
            'h-full',
            'z-10',
            'bg-grey-900/30',
            'cursor-pointer',
            'opacity-0',
            'group-[.-active]:opacity-100',
            'transition-all',
            ...(this.options.overlayClassNames || []),
        ];

        this.overlay = document.createElement('div');
        this.overlay.classList.add(...overlayClassNames);
        this.root.appendChild(this.overlay);

        const contentClassNames = [
            'relative',
            'z-20',
            'flex',
            'justify-end',
            'h-full',
            'translate-x-full',
            'pointer-events-none',
            '[&>div]:pointer-events-auto',
            'transition-all',
            'duration-500',
            'group-[.-active]:translate-x-0',
            'transition-all',
            'ease-cubic',
            ...(this.options.contentClassNames || []),
        ];

        this.content = document.createElement('div');
        this.content.classList.add(...contentClassNames);
        this.root.appendChild(this.content);

        document.body.appendChild(this.root);
    };

    open = () => {
        if (this.isOpen) {
            return;
        }

        this.isOpen = true;
        this.root.classList.add('-active');

        if (this.options.lockScroll) {
            Sidebar.lockPageScroll();
        }
    };

    close = () => {
        if (!this.isOpen) {
            return;
        }

        this.isOpen = false;
        this.root.classList.remove('-active');

        if (this.options.lockScroll) {
            Sidebar.unlockPageScroll();
        }
    };

    events = () => {
        this.overlay.addEventListener('click', this.close.bind(this));

        window.addEventListener('click', e => {
            const closeTrigger = e.target.closest('[js-close-sidebar]');

            if (closeTrigger && this.root.contains(closeTrigger)) {
                this.close();
            }
        });
    };
}
